export interface RateWindow<T> {
  rates: T[];
  /** index of the target rate within rates */
  targetIndex: number;
  /** true when every rate is shown, so no Show More is offered */
  isComplete: boolean;
}

/**
 * Picks count rates centred on the target, extra one above on odd counts.
 * A count of 0, null or undefined shows every rate. When the target sits near
 * either end the window slides so the user still sees count rates.
 * @param allRates the program's full rate stack
 * @param targetIdx index of the target rate within allRates
 * @param count clientSettings maxAlternates
 * @returns the windowed rates and the target's index within them
 */
export const selectRateWindow = <T>(
  allRates: T[],
  targetIdx: number,
  count: number | null | undefined,
): RateWindow<T> => {
  const total = allRates.length;
  const showAll = { rates: allRates, targetIndex: targetIdx, isComplete: true };

  if (!count || count <= 0) return showAll;
  if (targetIdx < 0 || targetIdx >= total) return showAll;
  if (count >= total) return showAll;

  const above = Math.ceil((count - 1) / 2);
  const below = count - 1 - above;

  let start = targetIdx - above;
  let end = targetIdx + below + 1;

  if (start < 0) {
    end += -start;
    start = 0;
  }
  if (end > total) {
    start -= end - total;
    end = total;
  }
  start = Math.max(0, start);

  return {
    rates: allRates.slice(start, end),
    targetIndex: targetIdx - start,
    isComplete: end - start >= total,
  };
};
